import { createElement, CSSProperties, ReactEventHandler, useEffect, useRef, useState } from "react";
import { captureException } from "@web-monorepo/telemetry";
import { thumbhashAspectRatio, thumbhashToDataUrl } from "./thumbhash.ts";

// thumbhashToDataUrl does real work — pixel reconstruction plus a PNG encode — over a small fixed
// set of literals (a generated ImageThumbhashes map), so caching by hash avoids redoing that on
// every render even when a caller passes an inline `style` object. (thumbhashAspectRatio is just
// a couple of bit reads on the hash header — cheap enough to call directly, no cache.) One cache
// shared by every app's Image component: thumbhash strings can't collide across apps in a way
// that matters, they're just cache keys.
const dataUrlCache = new Map<string, string>();
function cachedThumbhashDataUrl(hash: string): string {
  const cached = dataUrlCache.get(hash);
  if (cached !== undefined) return cached;
  const url = thumbhashToDataUrl(hash);
  dataUrlCache.set(hash, url);
  return url;
}

// Resolved URLs already reported as failing, so onError telemetry stays proportional to the number
// of broken paths rather than the number of elements rendering them. Shared across every app's
// Image component for the same reason the thumbhash cache is: the keys are absolute CDN URLs.
const reportedFailures = new Set<string>();
// Exported so tests drive their retry loops off the same budget the component enforces: a loop
// hardcoded to 3 would silently keep passing if this number ever shrank.
export const MAX_IMAGE_LOAD_ATTEMPTS = 3;

type ImageProps<S extends string, Thumbhashes> = Record<string, unknown> & {
  alt: string;
  src: S;
  preload?: boolean;
  style?: CSSProperties;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
} & (S extends keyof Thumbhashes
    ? {
        // Renders as a CSS background (plus a same-lifecycle `aspectRatio`, unless `style` already
        // sets one) behind the real <img> until it settles (loads or errors). The ratio is a
        // coarse, quantized approximation, not the image's real one, and this can only see a
        // caller's `style` — not `sx`/className sizing, which is how most call sites actually size
        // an <Image>. If the only dimension you constrain is via `sx`/className rather than
        // `style`, expect a one-time layout correction when the placeholder clears rather than a
        // permanently wrong box — that tradeoff is deliberate, see createImageComponent.ts.
        thumbhash?: Thumbhashes[S];
      }
    : { thumbhash?: never });

// Builds an <Image> component: `getImageSrc` resolves a literal path to the real CDN URL (see
// createImageSrcBuilder), and `Thumbhashes` is a generated, type-only ImageThumbhashes interface
// (e.g. `@web-monorepo/static-assets/generated/teach`) — passed as a type parameter, not a value,
// so the zero-runtime-cost guarantee that interface is built for carries all the way through:
// nothing about a caller's thumbhash literal is validated except by the type checker. Deliberately
// no `extends Record<string, string>` constraint here — a plain `export interface { "path":
// "hash"; ... }` (no index signature) doesn't structurally satisfy `Record<string, string>` as a
// generic constraint even though every value in it is a string; that's a real TypeScript
// limitation, not a meaningful safety net worth losing this over.
//
// `Paths` constrains `src` itself to a generated ImagePath union (every real object in the
// bucket, not just the thumbhashable subset — see ImagePath in generateImageThumbhashes.ts).
// Defaults to `string` so a caller can still opt out and pass any path, unchecked; the whole
// point of passing a real union here is that a typo'd or deleted path becomes a compile error
// instead of a silent runtime 404, so every current caller does. `getImageSrc: (path: string) =>
// string` still satisfies `(path: Paths) => string` when Paths narrows — parameter types are
// contravariant, so a function accepting anything can stand in for one accepting less.
export function createImageComponent<Thumbhashes, Paths extends string = string>(getImageSrc: (path: Paths) => string) {
  return function Image<S extends Paths>({
    src,
    alt,
    preload,
    thumbhash,
    style,
    onLoad,
    onError,
    ...rest
  }: ImageProps<S, Thumbhashes>) {
    const resolvedSrc = getImageSrc(src);
    const preloadRef = useRef<HTMLImageElement | null>(null);
    // Tracks which src has actually finished settling (loaded OR failed) rather than a plain
    // boolean, so a changed src is automatically treated as unsettled again with no extra effect
    // to sync it. React attaches onLoad/onError synchronously during the same commit that creates
    // this <img>, before the browser can dispatch its (always task-queued, never synchronous —
    // true even for a cache hit) load/error event, so there's no missed-event race to guard here.
    const [settledSrc, setSettledSrc] = useState<string | undefined>(undefined);
    const settled = settledSrc === resolvedSrc;
    const [retryKey, setRetryKey] = useState(0);
    const retryStateRef = useRef({ resolvedSrc, attempts: 0 });
    if (retryStateRef.current.resolvedSrc !== resolvedSrc) {
      retryStateRef.current = { resolvedSrc, attempts: 0 };
    }

    useEffect(() => {
      if (preload && resolvedSrc && !preloadRef.current) {
        const img = new window.Image();
        img.src = resolvedSrc;
        preloadRef.current = img;
      }
      return () => {
        preloadRef.current = null;
      };
    }, [preload, resolvedSrc]);

    // Cleared on `onLoad`/`onError` alike, so a broken image falls through to the browser's normal
    // broken-image affordance instead of leaving a permanent blurred box. (Transparent images and
    // SVGs never get a thumbhash in the first place — see computeThumbhashes in
    // generateImageThumbhashes.ts — so there's no path→hash entry for the compiler to accept here.)
    // See the `thumbhash` prop's own type comment above for the aspectRatio tradeoff.
    const placeholderStyle: CSSProperties | undefined =
      thumbhash && !settled
        ? {
            ...style,
            // Without a `Record<string, string>` constraint on `Thumbhashes` (see the comment on
            // createImageComponent above), `thumbhash`'s type here is an opaque generic, not
            // `string` — `String(...)` is a real runtime coercion (a no-op for the base64 string
            // it always actually is), not a type-system lie, so this stays honest without giving
            // up the zero-runtime-cost generated-interface design.
            backgroundImage: `url(${cachedThumbhashDataUrl(String(thumbhash))})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            aspectRatio: style?.aspectRatio ?? String(thumbhashAspectRatio(String(thumbhash))),
          }
        : style;

    // Plain createElement, not JSX — this file ships to apps whose bundlers may configure a
    // different JSX pragma/import source than plain React (teach's Vite config, for one, sets
    // esbuild's `jsxImportSource` to "@theme-ui/core" for every .tsx file it transforms, which
    // breaks a shared package authored with vanilla React JSX). createElement is unambiguous
    // plain JS, so it works the same regardless of which app's bundler is doing the transforming.
    return createElement("img", {
      key: `${resolvedSrc}:${retryKey}`,
      ...rest,
      src: resolvedSrc,
      style: placeholderStyle,
      alt,
      onLoad: (event: Parameters<NonNullable<typeof onLoad>>[0]) => {
        if (thumbhash) setSettledSrc(resolvedSrc);
        onLoad?.(event);
      },
      onError: (event: Parameters<NonNullable<typeof onError>>[0]) => {
        const retryState = retryStateRef.current;
        retryState.attempts += 1;
        if (retryState.attempts < MAX_IMAGE_LOAD_ATTEMPTS) {
          setRetryKey((current) => current + 1);
        } else {
          if (thumbhash) setSettledSrc(resolvedSrc);
          // The pre-CDN implementations checked the path against an eager glob and warned when it
          // wasn't there. There's no manifest to check against now — `src` is a plain string, and
          // the generated listing only types thumbhash values, not paths — so a typo or an object
          // that never got uploaded is otherwise an invisible broken <img>. This is the one place
          // that knows it actually failed.
          //
          // Deduped per URL for the lifetime of the page, not per element: a broken path is a
          // property of the path, so a list rendering it 50 times is one bug, not 50. Without this
          // a single bad key in a long list floods Sentry from four apps at once, live teach
          // included — and the signal we actually want is "which path is broken", which the first
          // report already carries.
          if (!reportedFailures.has(resolvedSrc)) {
            reportedFailures.add(resolvedSrc);
            captureException(new Error(`Image failed to load: ${resolvedSrc}`), {
              extra: { src, resolvedSrc },
            });
          }
          // Only the final give-up is forwarded: a caller's onError is a "this image is broken"
          // signal (swap in a fallback, set state), and firing it on each intermediate retry
          // would make callers flicker or do that work three times for one broken image.
          onError?.(event);
        }
      },
    });
  };
}
